import React, { useCallback, useMemo, useRef, useState } from "react";
import DispatchBoardGrid from "./DispatchBoardGrid";
import { MESSAGES } from "../../../common/Messages";
import { DEFAULT_LAYOUT_NAME } from "../constants";

/**
 * The top pane of one board tab.
 *
 * Pick, Del and Current differ only in which store they load from, which grid-layout key they save
 * under, and which dates and thresholds their colouring uses. Everything else - the layout state, the
 * selection hand-off to the bottom pane, the grid itself - is identical, so it lives here once and
 * each tab folder supplies its three parameters (Architecture.md R1).
 */
const BoardTabPane = (props) => {
    const {
        accessToken,
        topTab,
        gridKey,
        storeFactory,
        profile,
        profileResolved,
        onFocusedRowChanged,
        onSelectionChanged,
        showTripColumns,
    } = props;

    const [currentLayout, setCurrentLayout] = useState(DEFAULT_LAYOUT_NAME);

    // The store reads the profile code through a getter rather than a captured value, so changing
    // profile does not have to rebuild the store - the next load simply sends the new code. The ref
    // keeps that getter stable while still seeing the latest profile.
    const profileRef = useRef(profile);
    profileRef.current = profile;

    const getProfileCode = useCallback(() => profileRef.current?.ProfileCode ?? null, []);

    const dataSource = useMemo(
        () => storeFactory(accessToken, getProfileCode),
        [storeFactory, accessToken, getProfileCode]
    );

    // Two different notions of "current", both of which the Delphi has and the bottom pane needs:
    //
    //   focused row  -> the row Details and Contents follow (Delphi's ChangeNode / AfterScroll)
    //   selection    -> every row Totals sums (Delphi's SelectedCountChange)
    //
    // Keeping them separate is what lets a dispatcher ctrl-select six objects to read their combined
    // weight while the Details tab still shows the one row they are looking at.
    const handleFocusedRowChanged = useCallback(
        (e) => onFocusedRowChanged(e.row?.data ?? null),
        [onFocusedRowChanged]
    );

    const handleSelectionChanged = useCallback(
        (e) => onSelectionChanged(e.selectedRowsData ?? []),
        [onSelectionChanged]
    );

    // The getProfileCode getter above keeps the store current for every load AFTER the first - but
    // not for the first, which is the one that matters. The grid mounts and loads immediately, while
    // the profile is still in flight in a sibling effect, so that load goes out with profileCode="".
    //
    // On Pick and Del that is harmless: the profile only colours those tabs, and the colouring is
    // re-applied when it arrives. On Current it is not - the server reads CURRENTTABDATA from the
    // profile to decide whether to show trip-assigned rows, unassigned rows or all of them, and with
    // no profile code it falls back to unscoped. When DISPATCHINGTABDEFAULT is 'C' the Current tab is
    // also the landing tab, so that unscoped load is its first and, until the user pages or sorts, its
    // only one - the scope never applies.
    //
    // So the grid is not mounted until the profile lookup has settled. `profileResolved` is true for
    // "resolved to a profile" and for "resolved to none" alike: the board is expected to run
    // unnarrowed when no profile is configured (the Delphi's DispatchingProfile = '' path), and
    // waiting for a profile that will never come would leave the tab empty for good.
    //
    // Below the hooks, not above them - an early return before useCallback would change the hook
    // order between renders.
    if (!profileResolved) {
        return <div className="dm-placeholder">{MESSAGES.DISPATCH_LOADING_PROFILE}</div>;
    }

    return (
        <DispatchBoardGrid
            dataSource={dataSource}
            accessToken={accessToken}
            gridKey={gridKey}
            topTab={topTab}
            profile={profile}
            currentLayout={currentLayout}
            setCurrentLayout={setCurrentLayout}
            onFocusedRowChanged={handleFocusedRowChanged}
            onSelectionChanged={handleSelectionChanged}
            showTripColumns={showTripColumns}
        />
    );
};

export default BoardTabPane;
